/**
 * Sticky Action Bar for Ticket Reports
 *
 * Shows a sticky bar at the bottom of the viewport with the primary action
 * based on current ticket status. This helps users take action without
 * scrolling to the bottom of long pages.
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Typogaphy } from 'vnetwork-ui'
import { PlayCircle, Lock, ArrowUp, AlertTriangle } from 'lucide-react'
import { useModal } from '@/contexts/modal'
import { Ticket } from '@/modules/Ticket/types/response'
import {
    TICKET_STATUS_OPENING,
    TICKET_STATUS_WORK_IN_PROGRESS,
    TICKET_STATUS_COMPLETED,
    TICKET_STATUS_ESCALATED,
} from '@/modules/Ticket/constants'

interface StickyActionBarProps {
    ticket?: Ticket
    onScrollToForm?: () => void
    onEscalate?: () => void
}

const StickyActionBar: React.FC<StickyActionBarProps> = ({
    ticket,
    onScrollToForm,
    onEscalate,
}) => {
    const { t } = useTranslation()
    const { setModal } = useModal()

    const status = ticket?.ticket_status
    const isServiceTicket = ticket?.ticket_type_group === 'service'

    // Don't show for closed tickets
    if (status === 'closed') {
        return null
    }

    // Service ticket completed - show success state with consistent styling
    if (isServiceTicket && status === TICKET_STATUS_COMPLETED) {
        return (
            <div className="fixed bottom-0 left-0 right-0 z-50 bg-green-50 border-t border-green-200 shadow-lg">
                <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">
                                {t('ticket-current-status')}:
                            </span>
                            <span className="text-sm font-medium px-2 py-1 bg-green-100 text-green-700 rounded">
                                {t('ticket-status-completed')}
                            </span>
                        </div>
                        <Typogaphy
                            size="small"
                            className="text-gray-500 hidden sm:block"
                        >
                            #{ticket?.ticket_id}
                        </Typogaphy>
                    </div>
                    <span className="text-sm text-green-600">
                        ✓ {t('ticket-service-workflow-completed')}
                    </span>
                </div>
            </div>
        )
    }

    const handleStartProcessing = () => {
        setModal('TicketModals/StartTicketConfirm/index', {
            ticket,
        })
    }

    const getStatusConfig = () => {
        switch (status) {
            case TICKET_STATUS_OPENING:
                return {
                    label: t('ticket-status-opening'),
                    action: t('ticket-start-processing'),
                    icon: <PlayCircle className="w-4 h-4" />,
                    onClick: handleStartProcessing,
                    variant: 'contained' as const,
                }
            case TICKET_STATUS_WORK_IN_PROGRESS:
                return {
                    label: t('ticket-status-work-in-progress'),
                    action: t('ticket-scroll-to-form'),
                    icon: <ArrowUp className="w-4 h-4" />,
                    onClick: onScrollToForm,
                    variant: 'outlined' as const,
                    secondaryAction: onEscalate
                        ? {
                              label: 'Escalate',
                              icon: <AlertTriangle className="w-4 h-4" />,
                              onClick: onEscalate,
                              variant: 'outlined' as const,
                          }
                        : undefined,
                }
            case TICKET_STATUS_ESCALATED:
                return {
                    label: 'Escalated',
                    action: t('ticket-scroll-to-form'),
                    icon: <ArrowUp className="w-4 h-4" />,
                    onClick: onScrollToForm,
                    variant: 'outlined' as const,
                }
            case TICKET_STATUS_COMPLETED:
                return {
                    label: t('ticket-status-completed'),
                    action: t('ticket-pending-close'),
                    icon: <Lock className="w-4 h-4" />,
                    onClick: onScrollToForm,
                    variant: 'outlined' as const,
                }
            default:
                return null
        }
    }

    const config = getStatusConfig()
    if (!config) return null

    return (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
            <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
                {/* Status indicator */}
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">
                            {t('ticket-current-status')}:
                        </span>
                        <span className="font-medium text-sm px-2 py-1 bg-primary/10 text-primary rounded">
                            {config.label}
                        </span>
                    </div>
                    <Typogaphy
                        size="small"
                        className="text-gray-600 hidden sm:block"
                    >
                        #{ticket?.ticket_id}
                    </Typogaphy>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2">
                    {'secondaryAction' in config && config.secondaryAction && (
                        <Button
                            onClick={config.secondaryAction.onClick}
                            variant={config.secondaryAction.variant}
                            size="small"
                            startIcon={config.secondaryAction.icon}
                            className="!text-orange-600 !border-orange-300 hover:!bg-orange-50"
                        >
                            {config.secondaryAction.label}
                        </Button>
                    )}
                    <Button
                        onClick={config.onClick}
                        variant={config.variant}
                        size="small"
                        startIcon={config.icon}
                    >
                        {config.action}
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default StickyActionBar
